import { Entity, Engine } from "excalibur";
import { FC, useState, useEffect } from "react";
import { TargetableComponent } from "../ecs/target";
import { HudInterface } from "../hud";
import { CardPack, ResourceKind } from "../types";
import { AllTileOverlays } from "./AllTileOverlays";
import GameOver from "./GameOver";
import Help from "./Help";
import "./Hud.css";
import MobileDebugOverlay from "./MobileDebugOverlay";
import MobileSaveButton from "./MobileSaveButton";
import Shop from "./Shop";
import { TileHoverOverlay } from "./TileHoverOverlay";

export type HudProps = {
  fps: number;
  showShop: boolean;
  showGameOver: boolean;
  showHelp: boolean;
  forceShowOverlays?: boolean;
  hudController: HudInterface;
  hoverTarget: Entity<TargetableComponent> | undefined;
  game: Engine;
};

const Hud: FC<HudProps> = ({
  hoverTarget,
  hudController,
  showShop,
  showGameOver,
  showHelp,
  forceShowOverlays = false,
  game,
}) => {
  const [showAllOverlays, setShowAllOverlays] = useState(forceShowOverlays);
  const [showMobileDebugOverlay, setShowMobileDebugOverlay] = useState(false);
  const [, forceUpdate] = useState({});

  const getCurrentMoney = (): number => {
    return hudController?.getMoney?.() ?? 0;
  };

  const getCurrentTurnCount = (): number => {
    return hudController?.getTurnCount?.() || 1;
  };

  useEffect(() => {
    const handleRestore = () => {
      forceUpdate({}); // Force re-render when game state is restored
    };

    window.addEventListener("gameStateRestored", handleRestore);

    return () => {
      window.removeEventListener("gameStateRestored", handleRestore);
    };
  }, []);

  const getUnlockedResources = (): Set<ResourceKind> => {
    return hudController?.getUnlockedResources?.() || new Set();
  };

  const handleBuyPack = (pack: CardPack): boolean => {
    console.log("Buying pack:", pack.name);
    if (hudController?.buyPack) {
      return hudController.buyPack(pack);
    }
    return false;
  };

  const handleCloseShop = () => {
    // Notify the flow that shop should close
    if (hudController?.closeShop) {
      hudController.closeShop();
    }
  };

  const handleRestart = () => {
    if (hudController?.restartGame) {
      hudController.restartGame();
    }
  };

  const handleHelpClick = () => {
    if (hudController?.helpClicked) {
      hudController.helpClicked();
    }
  };

  const handleCloseHelp = () => {
    if (hudController?.closeHelp) {
      hudController.closeHelp();
    }
  };

  return (
    <div className="hud-container">
      <div className="hud-element hud-top-left">
        <div className="flex gap-2 items-center w-full">
          <div className="turn-counter">Turn {getCurrentTurnCount()}</div>
          <button
            className="shop-button clickable"
            onClick={() => setShowAllOverlays(!showAllOverlays)}
          >
            {showAllOverlays ? "Hide Info" : "Resources"}
          </button>
          <button className="shop-button clickable" onClick={handleHelpClick}>
            Help
          </button>
          {/* Flex spacer to push Save button to the right */}
          <div className="flex-1"></div>
          {/* Mobile Save Button - only visible on mobile, pushed to the right */}
          <MobileSaveButton onClick={() => setShowMobileDebugOverlay(true)} />
        </div>
      </div>
      <div className="hud-element hud-top-right"></div>
      {/* Tile overlay for building stats */}
      {showAllOverlays || forceShowOverlays ? (
        <AllTileOverlays hudInterface={hudController} />
      ) : (
        <TileHoverOverlay
          hudInterface={hudController}
          hoverTarget={hoverTarget}
        />
      )}
      {showShop && (
        <Shop
          onClose={handleCloseShop}
          onBuyPack={handleBuyPack}
          currentMoney={getCurrentMoney()}
          unlockedResources={getUnlockedResources()}
          isOutOfCards={hudController?.isShopOpenDueToOutOfCards || false}
        />
      )}
      {showGameOver && (
        <GameOver onRestart={handleRestart} finalMoney={getCurrentMoney()} />
      )}
      {showHelp && <Help onClose={handleCloseHelp} />}

      {/* Mobile Debug Overlay - only visible on mobile when open */}
      <MobileDebugOverlay
        game={game}
        isOpen={showMobileDebugOverlay}
        onClose={() => setShowMobileDebugOverlay(false)}
      />
    </div>
  );
};

export default Hud;
